import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { io, Socket } from 'socket.io-client';
import {
  DEV_SOCKET_READY_RETRY_MS,
  getSocketConfig,
  getSocketUrl,
  isReplitDevEnvironment,
  isSocketBackendReady,
} from '@/lib/socketUtils';
import { AUTH_STORAGE_KEYS } from '@/constants/auth';
import { refreshToken } from '@/lib/tokenRefresh';
import { useAuth } from './AuthContext';
import { browserLifecycle } from '@easyflow/ui-frontend';

interface SocketContextType {
  socket: Socket | null;
  isConnected: boolean;
  isAuthenticated: boolean;
  socketVersion: number;
  emit: (event: string, ...args: unknown[]) => void;
  on: <A extends unknown[] = unknown[]>(event: string, callback: (...args: A) => void) => void;
  off: <A extends unknown[] = unknown[]>(event: string, callback?: (...args: A) => void) => void;
  joinRoom: (room: string) => void;
  leaveRoom: (room: string) => void;
}

const SocketContext = createContext<SocketContextType | null>(null);

const MAX_AUTH_REFRESH_ATTEMPTS = 2;
const AUTH_COOLDOWN_MS = 5000;

export function useSocket(): SocketContextType {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error('useSocket must be used within a SocketProvider');
  }
  return context;
}

export function useSocketOptional(): SocketContextType | null {
  return useContext(SocketContext);
}

interface SocketProviderProps {
  children: React.ReactNode;
}

export function SocketProvider({ children }: SocketProviderProps) {
  const { user, isAuthenticated } = useAuth();
  const socketRef = useRef<Socket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [isSocketAuthenticated, setIsSocketAuthenticated] = useState(false);
  const [socketVersion, setSocketVersion] = useState(0);
  const subscribedRoomsRef = useRef<Set<string>>(new Set());
  const reconnectingRef = useRef(false);
  const isRefreshingSocketRef = useRef(false);
  const authRefreshAttemptsRef = useRef(0);
  const lastAuthErrorTimeRef = useRef(0);
  const authGaveUpRef = useRef(false);
  const handleAuthErrorRefreshRef = useRef<() => void>(() => {});
  const requestSocketConnectionRef = useRef<(tenantId: string) => void>(() => {});
  const devReconnectTimerRef = useRef<number | null>(null);
  const socketConnectionEnabledRef = useRef(false);

  const isAuthError = useCallback((message: string): boolean => {
    const authErrors = ['Invalid or expired token', 'Authentication required', 'jwt expired', 'invalid token'];
    return authErrors.some(err => message.toLowerCase().includes(err.toLowerCase()));
  }, []);

  const createSocket = useCallback((tenantId: string) => {
    const socketUrl = getSocketUrl();
    const socketConfig = getSocketConfig();

    // Task #2569 — riconnessione infinita con jitter dalla config condivisa
    // (`getSocketConfig`): niente piu` override 5 tentativi / 2000ms che
    // rendeva il socket morto-permanente dopo sleep/wake o cambio rete.
    const socket = io(socketUrl, {
      ...socketConfig,
      auth: (cb: (data: Record<string, string>) => void) => {
        const currentToken = localStorage.getItem(AUTH_STORAGE_KEYS.TOKEN);
        cb({ token: currentToken || '' });
      }
    });

    if (socket.io?.engine) {
      socket.io.engine.on('upgrade', (transport: { name: string }) => {
        console.log(`[SocketContext] Transport upgraded to: ${transport.name}`);
      });
    }

    socket.on('connect', () => {
      const transportName = socket.io?.engine?.transport?.name || 'unknown';
      console.log(`[SocketContext] Connected: ${socket.id} (transport: ${transportName})`);
      setIsConnected(true);
      setIsSocketAuthenticated(true);
      authRefreshAttemptsRef.current = 0;
      authGaveUpRef.current = false;

      if (reconnectingRef.current) {
        console.log('[SocketContext] Reconnected - re-subscribing rooms');
        subscribedRoomsRef.current.forEach(room => {
          socket.emit(`subscribe:${room}`, tenantId);
        });
        reconnectingRef.current = false;
        setSocketVersion(v => v + 1);
      }
    });

    socket.on('disconnect', (reason) => {
      console.log('[SocketContext] Disconnected:', reason);
      setIsConnected(false);
      if (isReplitDevEnvironment() && reason !== 'io client disconnect') {
        reconnectingRef.current = true;
        if (socketRef.current === socket) {
          socketRef.current = null;
        }
        socket.removeAllListeners();
        requestSocketConnectionRef.current(tenantId);
        return;
      }

      if (reason === 'io server disconnect') {
        // Disconnessione iniziata dal server (deploy/restart o revoca sessione).
        // Socket.IO NON riconnette da solo in questo caso: ritentiamo a mano,
        // salvo che l'auth sia gia` stata definitivamente esaurita (force-logout).
        // Un'eventuale sessione revocata fallira` su connect_error -> refresh.
        if (authGaveUpRef.current) {
          setIsSocketAuthenticated(false);
        } else {
          reconnectingRef.current = true;
          socket.connect();
        }
      } else {
        reconnectingRef.current = true;
      }
    });

    socket.on('connect_error', (error) => {
      setIsConnected(false);

      if (isAuthError(error.message)) {
        console.error('[SocketContext] Connection authentication error:', error.message);
        const now = Date.now();
        if (now - lastAuthErrorTimeRef.current < AUTH_COOLDOWN_MS) {
          console.log('[SocketContext] Auth error cooldown active, ignoring');
          socket.disconnect();
          return;
        }
        lastAuthErrorTimeRef.current = now;

        socket.disconnect();

        if (authGaveUpRef.current) {
          return;
        }

        if (authRefreshAttemptsRef.current >= MAX_AUTH_REFRESH_ATTEMPTS) {
          console.warn('[SocketContext] Max auth refresh attempts reached, stopping');
          setIsSocketAuthenticated(false);
          authGaveUpRef.current = true;
          return;
        }

        if (!isRefreshingSocketRef.current) {
          console.log('[SocketContext] Auth error, refreshing token...');
          handleAuthErrorRefreshRef.current();
        }
        return;
      }

      if (isReplitDevEnvironment()) {
        // Un backend in boot/restart non conserva il sid polling precedente.
        // Non lasciamo che Socket.IO bombardi il proxy Vite con POST destinati
        // al 400: chiudiamo il client e attendiamo il readiness HTTP.
        console.warn('[SocketContext] Dev backend unavailable; waiting for readiness before reconnecting');
        reconnectingRef.current = true;
        if (socketRef.current === socket) {
          socketRef.current = null;
        }
        socket.removeAllListeners();
        requestSocketConnectionRef.current(tenantId);
        return;
      }

      console.error('[SocketContext] Connection error:', error.message);
    });

    socket.on('session:force-logout', (data: { message: string; timestamp: string }) => {
      console.log('[SocketContext] Force logout received:', data.message);
      window.dispatchEvent(new CustomEvent('session:force-logout', { detail: data }));
    });

    return socket;
  }, [isAuthError]);

  const destroySocket = useCallback(() => {
    if (socketRef.current) {
      socketRef.current.removeAllListeners();
      socketRef.current.disconnect();
      socketRef.current = null;
    }
  }, []);

  const clearDevReconnectTimer = useCallback(() => {
    if (devReconnectTimerRef.current !== null) {
      window.clearTimeout(devReconnectTimerRef.current);
      devReconnectTimerRef.current = null;
    }
  }, []);

  const requestSocketConnection = useCallback((tenantId: string) => {
    if (!socketConnectionEnabledRef.current || socketRef.current) {
      return;
    }

    if (!isReplitDevEnvironment()) {
      socketRef.current = createSocket(tenantId);
      return;
    }

    if (devReconnectTimerRef.current !== null) {
      return;
    }

    const connectWhenReady = async (): Promise<void> => {
      const ready = await isSocketBackendReady();
      if (!socketConnectionEnabledRef.current || socketRef.current) {
        return;
      }

      if (ready) {
        socketRef.current = createSocket(tenantId);
        return;
      }

      devReconnectTimerRef.current = window.setTimeout(() => {
        devReconnectTimerRef.current = null;
        void connectWhenReady();
      }, DEV_SOCKET_READY_RETRY_MS);
    };

    void connectWhenReady();
  }, [createSocket]);

  requestSocketConnectionRef.current = requestSocketConnection;

  handleAuthErrorRefreshRef.current = useCallback(async () => {
    if (isRefreshingSocketRef.current) {
      return;
    }

    isRefreshingSocketRef.current = true;
    authRefreshAttemptsRef.current += 1;
    const attempt = authRefreshAttemptsRef.current;
    console.log(`[SocketContext] Auth refresh attempt ${attempt}/${MAX_AUTH_REFRESH_ATTEMPTS}`);

    try {
      destroySocket();

      const outcome = await refreshToken();
      if (outcome.kind === 'refreshed') {
        console.log('[SocketContext] Token refreshed, creating new socket');
        const tenantId = user?.tenant_id;
        if (tenantId) {
          reconnectingRef.current = true;
          requestSocketConnectionRef.current(tenantId);
        }
      } else {
        console.log('[SocketContext] Token refresh failed - session expired');
        setIsSocketAuthenticated(false);
        authGaveUpRef.current = true;
      }
    } catch (error) {
      console.error('[SocketContext] Token refresh error:', error);
      setIsSocketAuthenticated(false);
      authGaveUpRef.current = true;
    } finally {
      isRefreshingSocketRef.current = false;
    }
  }, [destroySocket, user?.tenant_id]);

  useEffect(() => {
    if (!isAuthenticated || !user?.tenant_id) {
      socketConnectionEnabledRef.current = false;
      clearDevReconnectTimer();
      if (socketRef.current) {
        console.log('[SocketContext] Disconnecting - user not authenticated');
        destroySocket();
        setIsConnected(false);
        setIsSocketAuthenticated(false);
      }
      return;
    }

    const token = localStorage.getItem(AUTH_STORAGE_KEYS.TOKEN);
    if (!token) {
      console.log('[SocketContext] No token available, skipping connection');
      return;
    }

    socketConnectionEnabledRef.current = true;
    authRefreshAttemptsRef.current = 0;
    authGaveUpRef.current = false;
    console.log('[SocketContext] Creating socket connection...');
    requestSocketConnection(user.tenant_id);

    const subscribedRoomsRefSnapshot = subscribedRoomsRef;
    return () => {
      console.log('[SocketContext] Cleanup - disconnecting socket');
      socketConnectionEnabledRef.current = false;
      clearDevReconnectTimer();
      destroySocket();
      setIsConnected(false);
      setIsSocketAuthenticated(false);
      authRefreshAttemptsRef.current = 0;
      authGaveUpRef.current = false;
      subscribedRoomsRefSnapshot.current.clear();
    };
  }, [isAuthenticated, user?.tenant_id, clearDevReconnectTimer, destroySocket, requestSocketConnection]);

  // Task #2569 — nudge lifecycle: al ritorno-in-primo-piano / online, se il
  // socket esiste ma e` caduto (sleep/wake, cambio rete, NAT rebind), forza
  // subito un tentativo di riconnessione invece di aspettare il prossimo tick
  // di backoff. Socket.IO de-duplica se gia` connesso/connecting.
  useEffect(() => {
    return browserLifecycle.subscribe((event) => {
      if (event.type === 'offline') return;
      const socket = socketRef.current;
      if (isReplitDevEnvironment() && !authGaveUpRef.current && user?.tenant_id) {
        reconnectingRef.current = true;
        requestSocketConnectionRef.current(user.tenant_id);
        return;
      }
      if (socket && !socket.connected && !authGaveUpRef.current) {
        console.log('[SocketContext] Lifecycle nudge - forcing reconnect');
        reconnectingRef.current = true;
        socket.connect();
      }
    });
  }, [user?.tenant_id]);

  useEffect(() => {
    const handleTokenRefreshed = (event: Event) => {
      const customEvent = event as CustomEvent<{ token: string }>;
      const newToken = customEvent.detail?.token;
      if (!newToken) return;

      if (isRefreshingSocketRef.current) {
        return;
      }

      if (socketRef.current?.connected) {
        return;
      }

      if (authGaveUpRef.current) {
        console.log('[SocketContext] External token refresh received - retrying connection');
        authRefreshAttemptsRef.current = 0;
        authGaveUpRef.current = false;
        destroySocket();

        const tenantId = user?.tenant_id;
        if (tenantId) {
          reconnectingRef.current = true;
          requestSocketConnectionRef.current(tenantId);
        }
      }
    };

    window.addEventListener('auth:token-refreshed', handleTokenRefreshed);
    return () => {
      window.removeEventListener('auth:token-refreshed', handleTokenRefreshed);
    };
  }, [createSocket, destroySocket, user?.tenant_id]);

  const emit = useCallback((event: string, ...args: unknown[]) => {
    if (socketRef.current?.connected) {
      socketRef.current.emit(event, ...args);
    }
  }, []);

  const on = useCallback(<A extends unknown[] = unknown[]>(event: string, callback: (...args: A) => void) => {
    if (socketRef.current) {
      socketRef.current.on(event, callback as (...args: unknown[]) => void);
    }
  }, []);

  const off = useCallback(<A extends unknown[] = unknown[]>(event: string, callback?: (...args: A) => void) => {
    if (socketRef.current) {
      if (callback) {
        socketRef.current.off(event, callback as (...args: unknown[]) => void);
      } else {
        socketRef.current.off(event);
      }
    }
  }, []);

  const joinRoom = useCallback((room: string) => {
    if (socketRef.current?.connected && user?.tenant_id) {
      socketRef.current.emit(`subscribe:${room}`, user.tenant_id);
      subscribedRoomsRef.current.add(room);
    }
  }, [user?.tenant_id]);

  const leaveRoom = useCallback((room: string) => {
    if (socketRef.current?.connected && user?.tenant_id) {
      socketRef.current.emit(`unsubscribe:${room}`, user.tenant_id);
      subscribedRoomsRef.current.delete(room);
    }
  }, [user?.tenant_id]);

  const value: SocketContextType = {
    socket: socketRef.current,
    isConnected,
    isAuthenticated: isSocketAuthenticated,
    socketVersion,
    emit,
    on,
    off,
    joinRoom,
    leaveRoom
  };

  return (
    <SocketContext.Provider value={value}>
      {children}
    </SocketContext.Provider>
  );
}
